<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import { $getSignPdf, $getSignPdfTaskStatus } from '@/api/signs'

interface IProps {
  signId?: number | string | null,
  printBtnLoading?: boolean,
  isPrintBtnDisabled?: boolean,
}

const props = withDefaults(defineProps<IProps>(), {
  signId: null,
  printBtnLoading: false,
  isPrintBtnDisabled: false,
})

interface CustomEvents {
  (e: 'update:printBtnLoading', value: boolean): void
}

const emit = defineEmits<CustomEvents>()

const snackbarStore = useSnackbarStore()

const showSignExportMenu = ref<boolean>(false)
const exportCategoryOptions = ref([
  { title: 'Sign Details PDF', value: 'default'},
  { title: 'Install PDF', value: 'installation'}
])

/**
 * Generates a PDF document based on the specified type
 * @param {'default'|'installation'} [pdfType='default'] - The type of PDF to generate:
 *   - 'default': Standard PDF output
 *   - 'installation': Installation content PDF
 */
const generatePdf = async (pdfType: 'default'|'installation' = 'default') => {
  if (!props.signId) return

  emit('update:printBtnLoading', true)
  showSignExportMenu.value = false

  const params = {
    signs: props.signId,
    pdf_type: pdfType
  }
  try {
    // Start the PDF generation task
    const response = await $getSignPdf(params)
    const taskId = response.data.task_id

    // Poll for task completion
    const checkTaskStatus = async () => {
      try {
        const statusResponse = await $getSignPdfTaskStatus(taskId)
        if (statusResponse.data.status === 'success') {
          // Download the PDF from S3
          window.open(statusResponse.data.file_url, '_blank')
          emit('update:printBtnLoading', false)
        } else if (statusResponse.data.status === 'processing') {
          // Continue polling
          setTimeout(checkTaskStatus, 1000)
        } else {
          // Handle error
          emit('update:printBtnLoading', false)
          snackbarStore.showMessage({
            color: 'error',
            icon: 'mdi-alert-circle',
            title: 'Error',
            text: 'Failed to generate PDF'
          })
        }
      } catch (error) {
        emit('update:printBtnLoading', false)
        snackbarStore.showMessage({
          color: 'error',
          icon: 'mdi-alert-circle',
          title: 'Error',
          text: 'Failed to check PDF generation status'
        })
      }
    }

    // Start polling
    checkTaskStatus()
  } catch (error) {
    emit('update:printBtnLoading', false)
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: 'Failed to start PDF generation'
    })
  }
}
</script>

<template>
  <VMenu
    v-model="showSignExportMenu"
    :close-on-back="true"
    :close-on-content-click="false"
    :open-on-click="true"
  >
    <template #activator="{ props }">
      <VBtn
        :disabled="!!isPrintBtnDisabled"
        class="ma-1"
        color="default"
        v-bind="props"
        variant="tonal"
      >
        <VProgressCircular
          v-if="printBtnLoading"
          size="x-small"
          :width="3"
          indeterminate
          color="light"
          class="mr-1"
        />
        Print
      </VBtn>
    </template>
    <VCard class="sign-dialog-print-block-export-menu-body">
      <VList
        class="py-1"
      >
        <VListItem
          v-for="(exportOption, exportOptionKey) in exportCategoryOptions"
          :key="exportOptionKey"
          :title="exportOption.title"
          class="py-1 mx-1"
          @click="generatePdf(exportOption.value)"
        />
      </VList>
    </VCard>
  </VMenu>
</template>

<style lang="scss" scoped>
.sign-dialog-print-block-export-menu-body {
  box-shadow: rgba(0, 0, 0, 0.24) 0 0 4px !important;
  .v-list .v-list-item {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border-radius: 0 !important;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }
  .v-list .v-list-item:first-child {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border-top-left-radius: inherit !important;
    border-top-right-radius: inherit !important;
  }
  .v-list .v-list-item:last-child {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border-bottom-left-radius: inherit !important;
    border-bottom-right-radius: inherit !important;
    border-bottom: 0;
  }
}
</style>
